import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { RefreshCw, Activity, Clock, CheckCircle, AlertCircle, Loader2, Brain } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallMasterPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [grading, setGrading] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/sync-status`, { headers })
      .then(r => setDomains(r.data))
      .catch(() => toast.error('Failed to load sync status'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);  

  const triggerSync = async (domainId, domainName) => {
    setSyncing(s => ({ ...s, [domainId]: true }));
    try {
      await axios.post(`${API}/admin/domains/${domainId}/sync`, {}, { headers });
      toast.success(`Sync started for ${domainName}`);
      setTimeout(load, 3000);
    } catch {
      toast.error('Failed to trigger sync');
    } finally {
      setSyncing(s => ({ ...s, [domainId]: false }));
    }
  };

  const triggerGradePending = async (domainId, domainName, pendingCount) => {
    if (!window.confirm(`Grade ${pendingCount} pending calls for ${domainName}? This runs in the background.`)) return;
    setGrading(s => ({ ...s, [domainId]: true }));
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/grade-pending`, {}, { headers });
      if (r.data.pending === 0) {
        toast.info(r.data.message);
      } else {
        toast.success(`${r.data.message} Check the domain detail for progress.`);
      }
    } catch {
      toast.error('Failed to start grading');
    } finally {
      setGrading(s => ({ ...s, [domainId]: false }));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleString();
  };

  const goToCalls = (domainId, status = null) => {
    const params = new URLSearchParams({ tab: 'calls' });
    if (status) params.set('status', status);
    navigate(`/admin/domains/${domainId}?${params}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Call Master</h1>
            <p className="text-white/40 text-sm mt-1">Monitor and control call sync for all domains</p>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-sm rounded-lg transition-colors"
            data-testid="refresh-sync-status-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-white/20 animate-spin" />
          </div>
        ) : domains.length === 0 ? (
          <div className="text-center py-24 text-white/20">
            <Activity className="w-12 h-12 mx-auto mb-3" />
            <p>No domains configured yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {domains.map(d => (
              <div key={d.domain_id} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`call-master-row-${d.domain_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-semibold">{d.company_name || d.yabbit_domain}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        d.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        d.status === 'syncing' ? 'bg-yellow-500/20 text-yellow-400 animate-pulse' :
                        'bg-white/10 text-white/30'
                      }`}>
                        {d.status === 'syncing' ? 'Syncing...' : d.status}
                      </span>
                    </div>
                    <p className="text-white/30 text-xs mb-4">{d.yabbit_domain} · {d.industry?.replace(/_/g, ' ')} · {d.extensions_count} extensions</p>

                    {/* Stats — clickable to drill into calls */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button
                        onClick={() => goToCalls(d.domain_id)}
                        className="bg-white/5 rounded-lg p-3 text-left hover:bg-white/10 transition-colors cursor-pointer group"
                        data-testid={`cm-total-${d.domain_id}`}
                        title="View all calls"
                      >
                        <p className="text-white text-lg font-bold group-hover:text-teal-300 transition-colors">{d.total_calls}</p>
                        <p className="text-white/30 text-xs">Total Calls</p>
                      </button>
                      <button
                        onClick={() => goToCalls(d.domain_id, 'analyzed')}
                        className="bg-green-500/10 rounded-lg p-3 text-left hover:bg-green-500/20 transition-colors cursor-pointer group"
                        data-testid={`cm-analyzed-${d.domain_id}`}
                        title="View analyzed calls"
                      >
                        <p className="text-green-400 text-lg font-bold group-hover:text-green-300 transition-colors">{d.analyzed}</p>
                        <p className="text-white/30 text-xs">Analyzed</p>
                      </button>
                      <button
                        onClick={() => goToCalls(d.domain_id, 'pending')}
                        className="bg-yellow-500/10 rounded-lg p-3 text-left hover:bg-yellow-500/20 transition-colors cursor-pointer group"
                        data-testid={`cm-pending-${d.domain_id}`}
                        title="View pending calls"
                      >
                        <p className="text-yellow-400 text-lg font-bold group-hover:text-yellow-300 transition-colors">{d.pending}</p>
                        <p className="text-white/30 text-xs">Pending</p>
                      </button>
                      <button
                        onClick={() => goToCalls(d.domain_id, 'escalated')}
                        className="bg-red-500/10 rounded-lg p-3 text-left hover:bg-red-500/20 transition-colors cursor-pointer group"
                        data-testid={`cm-escalated-${d.domain_id}`}
                        title="View escalated calls"
                      >
                        <p className="text-red-400 text-lg font-bold group-hover:text-red-300 transition-colors">{d.escalated}</p>
                        <p className="text-white/30 text-xs">Escalated</p>
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => triggerSync(d.domain_id, d.company_name || d.yabbit_domain)}
                      disabled={syncing[d.domain_id] || d.status === 'syncing'}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid={`sync-btn-${d.domain_id}`}
                    >
                      {syncing[d.domain_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Now
                    </button>
                    {d.gradeable > 0 && (
                      <button
                        onClick={() => triggerGradePending(d.domain_id, d.company_name || d.yabbit_domain, d.gradeable)}
                        disabled={grading[d.domain_id]}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid={`grade-pending-btn-${d.domain_id}`}
                      >
                        {grading[d.domain_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                        Grade {d.gradeable} Pending
                      </button>
                    )}
                    <div className="text-right">
                      <p className="text-white/20 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last sync: {formatDate(d.last_sync)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CallMasterPage;
